export interface JWTPayload {
  sub: string;
  purpose: string;
  exp: number;
  iat: number;
  nbf: number;
  email?: string;
  [key: string]: unknown;
}

export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');

    if (parts.length !== 3) {
      console.error('Invalid JWT token format - expected 3 parts, got:', parts.length);
      return null;
    }

    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
}

export function getUserIdFromToken(token: string): string | null {
  const payload = decodeJWT(token);
  const userId = payload?.sub || null;  // Use 'sub' field instead of 'user_id'
  return userId;
}

export function isTokenExpired(token: string): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true; // Consider expired if no expiration is found
  }
  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();

  return currentTime >= expirationTime;
}

/**
 * Storage key for storing user email in localStorage
 */
const STORED_EMAIL_KEY = 'nomu_last_email';

/**
 * Store email in localStorage for biometric login auto-prompt
 */
export function storeEmailForBiometric(email: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORED_EMAIL_KEY, email);
  } catch (error) {
    console.warn('Failed to store email for biometric login:', error);
  }
}

/**
 * Get stored email from localStorage
 */
export function getStoredEmail(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(STORED_EMAIL_KEY);
  } catch (error) {
    console.warn('Failed to get stored email:', error);
    return null;
  }
}

/**
 * Clear stored email from localStorage
 */
export function clearStoredEmail(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORED_EMAIL_KEY);
  } catch (error) {
    console.warn('Failed to clear stored email:', error);
  }
}

export function getEmailFromAccessTokenPayload(token: string): string | null {
  const payload = decodeJWT(token);
  const em = typeof payload?.email === 'string' ? payload.email.trim() : '';
  return em || null;
}

/**
 * Email from a JWT stored in localStorage (if present), regardless of expiry — display hint only.
 */
export function getEmailFromStoredAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) return null;
    return getEmailFromAccessTokenPayload(accessToken);
  } catch (error) {
    console.warn('Failed to read email from stored access token:', error);
    return null;
  }
}

export function persistEmailFromAccessToken(token: string): void {
  const em = getEmailFromAccessTokenPayload(token);
  if (em) storeEmailForBiometric(em);
}

